import { useEffect, useState } from "react";
import { Download, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

export const StudioImage = ({ kind, id, alt, className = "", testId }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false, objectUrl;
    setUrl(null); setError(false);
    api.studioFile(kind, id).then((blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob); setUrl(objectUrl);
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [kind, id, retry]);
  if (error) return <span data-testid={`${testId}-error`} className="grid place-items-center p-3 text-xs text-red-300">Preview unavailable <button type="button" data-testid={`${testId}-retry`} onClick={(e) => { e.stopPropagation(); setRetry((n) => n + 1); }} className="flex gap-1 mt-1 hover:text-white"><RefreshCw size={12} />Retry</button></span>;
  return url ? <img data-testid={testId} src={url} alt={alt} className={className} /> : <span data-testid={`${testId}-loading`} className="grid place-items-center h-full min-h-12"><Loader2 size={18} className="text-zinc-500 animate-spin" /></span>;
};

export const downloadStudioFile = async (kind, item) => {
  const blob = await api.studioFile(kind, item.id);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${(item.title || item.name || "design").replace(/\.[^.]+$/, "").replace(/[^a-z0-9]+/gi, "-").slice(0, 80)}.png`;
  document.body.appendChild(anchor); anchor.click(); anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

export const CanvaHandoff = ({ design, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try { await downloadStudioFile("designs", design); toast.success("PNG downloaded — upload it in Canva"); }
    catch { toast.error("Could not download your design"); }
    finally { setDownloading(false); }
  };
  return <Dialog open={!!design} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent data-testid="canva-instructions-modal" className="bg-[#0b0e14] border-cyan-400/25 text-zinc-100 max-w-md [&>button:last-child]:hidden">
      <DialogTitle className="font-tactical uppercase tracking-wider text-cyan-300">Continue in Canva</DialogTitle>
      <DialogDescription className="text-zinc-400">A manual handoff. LifeOS is not connected to your Canva account.</DialogDescription>
      <ol data-testid="canva-handoff-instructions" className="space-y-4 text-sm text-zinc-300 list-decimal pl-5 marker:text-cyan-300">
        <li>Download your saved PNG below.</li>
        <li>Open Canva and create a design using <span className="text-white">{design?.width} × {design?.height} px</span> custom dimensions.</li>
        <li>Choose <span className="text-white">Uploads → Upload files</span>, then add the PNG to your canvas.</li>
      </ol>
      <p className="text-xs text-amber-200/80 bg-amber-500/10 rounded-lg p-3">This is a flattened image, not editable Canva layers. You can add new text and elements on top. Check AI-generated spelling and details before publishing.</p>
      <Button data-testid="canva-download-png" disabled={downloading} onClick={download} className="bg-cyan-500 text-black hover:bg-cyan-400">{downloading ? <Loader2 size={15} className="animate-spin mr-2" /> : <Download size={15} className="mr-2" />} Download PNG</Button>
      <a data-testid="canva-open-link" href={process.env.REACT_APP_CANVA_URL} target="_blank" rel="noreferrer" className="flex justify-center items-center gap-2 py-2.5 border border-white/15 rounded-lg text-sm hover:bg-white/5 transition-colors">Open Canva <ExternalLink size={14} /></a>
      <button data-testid="canva-close-button" onClick={onClose} className="text-xs text-zinc-500 hover:text-white">Back to studio</button>
    </DialogContent>
  </Dialog>;
};
